export type IntakeSource = "Intake Form" | "Leadership" | "OAIP Internal" | "Other";

export const INTAKE_SOURCES: IntakeSource[] = [
  "Intake Form",
  "Leadership",
  "OAIP Internal",
  "Other",
];

export interface IntakeAnswer {
  questionId: string;
  stepTitle?: string;
  question?: string;
  answer?: string;
}

export interface IntakeItem {
  id: string;
  type?: string;
  status?: string;
  createdAt?: string;
  submittedAt?: string;
  intakeName?: string;
  intakeDescription?: string;
  problemBeingSolved?: string;
  currentProblemImpact?: string;
  notes?: string;
  submitterName?: string;
  submitterEmail?: string;
  submitterAgency?: string;
  source?: IntakeSource;
  summaryAnswers?: IntakeAnswer[];
  intakeSchema?: Array<{
    id?: string;
    questionId?: string;
    stepTitle?: string;
    question?: string;
  }>;
  rejectionReason?: string;
  onHoldReason?: string;
  archived?: boolean;
  convertedProjectId?: string;
  convertedAt?: string;
  suggestedVendors?: Array<{
    companyName?: string;
    name?: string;
    relevance?: number | string;
    explanation?: string;
  }>;
}

export interface VendorRef {
  id: string;
  profile?: string;
  capabilityTags?: string[];
  status?: string;
}

export interface CommentItem {
  id: string;
  intakeId: string;
  message: string;
  createdAt?: string;
  author?: string;
}

export type ScoreValue = 1 | 2 | 3 | 4 | 5;

export interface RubricQuestion {
  id: string;
  text: string;
  anchors: Record<"1" | "3" | "5", string>;
  helpText?: string;
}

export interface RubricCriterion {
  id: string;
  name: string;
  questions: RubricQuestion[];
}

export interface RubricCategory {
  id: string;
  name: string;
  criteria: RubricCriterion[];
}

export interface ScoringRubric {
  version: string | number;
  categories: RubricCategory[];
}

export interface CriterionMean {
  mean: number;
  count: number;
  questionsScored?: number;
  questionsTotal?: number;
}

export interface CategoryMean extends CriterionMean {
  questionsScored: number;
  questionsTotal: number;
}

export interface QuestionMean {
  mean: number;
  count: number;
}

export interface ScoringAggregate {
  intakeId: string;
  rubricVersion: string | number;
  questionMeans: Record<string, QuestionMean>;
  criterionMeans: Record<string, CriterionMean>;
  categoryMeans: Record<string, CategoryMean>;
  overallMean: number;
  evaluatorUid?: string;
  evaluatorName?: string;
  evaluatorEmail?: string;
  submittedAt?: string;
  overallNotes?: string;
  computedAt: string;
}

export interface ScoringEvaluation {
  id: string;
  intakeId: string;
  evaluatorUid: string;
  evaluatorEmail?: string;
  evaluatorName?: string;
  rubricVersion: string | number;
  scores: Record<string, { score: ScoreValue; notes?: string }>;
  overallNotes?: string;
  submittedAt?: string;
  status?: string;
}

export type WorkflowStageId =
  | "schedule_meeting"
  | "scoring_chart"
  | "vendor_matching"
  | "ai_advisory_board_presentation"
  | "leadership_presentation"
  | "approval_decision"
  | "completed";

export type VendorSelection =
  | {
      type: "vendor";
      vendorId: string;
      vendorName: string;
      selectedAt?: string;
      selectedBy?: string;
      selectedByEmail?: string;
      selectedByName?: string;
    }
  | {
      type: "build_internal";
      reason?: string;
      selectedAt?: string;
      selectedBy?: string;
      selectedByEmail?: string;
      selectedByName?: string;
    };

export type RiskHarmLevel = "Minor" | "Moderate" | "Major" | "Severe";
export type RiskDataClassification =
  | "Public"
  | "Internal"
  | "Confidential"
  | "Restricted";
export type RiskLevel = "Low" | "Medium" | "High" | "Very High";

export interface RiskAssessment {
  harmToAgency: RiskHarmLevel;
  harmToPublic: RiskHarmLevel;
  dataClassification: RiskDataClassification;
  /** Server-derived from the matrix; never trust a client-supplied value. */
  riskLevel: RiskLevel;
  updatedAt?: string;
  updatedByEmail?: string;
}

export interface WorkflowStage {
  id: WorkflowStageId;
  name: string;
  status: "pending" | "in-progress" | "completed";
  completedAt?: string;
  completedBy?: string;
  /** AI Advisory Board meeting notes (used for stage gating + leadership brief context) */
  notes?: string;
  /** Leadership Presentation: confirms the brief was sent before advancing */
  briefSentToLeadership?: boolean;
  /** Risk Assessment captured on the scoring stage; required to advance past scoring (OAIP-91). */
  riskAssessment?: RiskAssessment;
}

export interface WorkflowDecision {
  decision: "approved" | "declined" | "deferred";
  rationale: string;
  decidedAt: string;
  decidedBy?: string;
  decidedByEmail?: string;
}

export interface IntakeWorkflow {
  id: string;
  intakeId: string;
  currentStage: WorkflowStageId;
  stages: WorkflowStage[];
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  decision?: WorkflowDecision;
  selectedVendor?: VendorSelection;
}

export interface WorkflowMeeting {
  id: string;
  intakeId: string;
  workflowId?: string;
  meetingDate: string;
  meetingType: string;
  status: "scheduled" | "completed" | "canceled";
  attendees: string[];
  notes?: string;
  createdAt?: string;
}

export interface WorkflowBrief {
  id: string;
  intakeId: string;
  briefType: "ai_advisory_board" | "leadership";
  markdown: string;
  model?: string;
  generatedAt?: string;
  generatedBy?: string;
  generatedByEmail?: string;
  rubricVersion?: number;
  /** OAIP-76 — populated by `apexBriefUpdateHandler` after a staff edit. */
  editedAt?: string;
  /** OAIP-76 — populated by `apexBriefUpdateHandler` after a staff edit. */
  editedByEmail?: string;
}

export interface WorkflowBundle {
  workflow: IntakeWorkflow | null;
  meetings: WorkflowMeeting[];
  evaluation: ScoringEvaluation | null;
  aggregate: ScoringAggregate | null;
  briefs: WorkflowBrief[];
  rubric: ScoringRubric | null;
}

export const STAGE_LABELS: Record<WorkflowStageId, string> = {
  schedule_meeting: "Schedule Meeting",
  scoring_chart: "Fill Scoring Chart",
  vendor_matching: "Select Vendor",
  ai_advisory_board_presentation: "AI Advisory Board",
  leadership_presentation: "Leadership Presentation",
  approval_decision: "Approval Decision",
  completed: "Completed",
};
